import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-8 md:pt-12 pb-2 md:pb-3 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <RichTextContent
            path="propertyoverview.pre_title"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] mb-6 tracking-wide uppercase"
          />

          <RichTextContent
            path="propertyoverview.title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold overview-title-gradient mb-4 leading-tight uppercase"
          />

          <div className="w-20 h-1 mx-auto mb-8 rounded-full overview-accent-bar" />

          <RichTextContent
            path="propertyoverview.description"
            as="div"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto leading-relaxed [&_a]:text-primary [&_a]:underline"
          />

          <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className="inline-block text-center mt-8 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-text)] hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] transition-all"
            >
              {ctaLink.label || 'Explore Floor Plans'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .overview-title-gradient {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .overview-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
